import { Editor } from "@tinymce/tinymce-react"
import { useEffect, useRef, useState } from "react"

import InsertLinkDialog from "./InsertLinkDialog"
import {
  stripEmbeddedEmailSignature,
  stripTemplateSignaturePreview,
  syncEditorSignaturePreview,
} from "./emailSignatureUtils"

export default function EmailWYSWIGEditor({
  editorRef,
  setEditorReady,
  editorReady = false,
  insertLinkDialogOpen = false,
  setInsertLinkDialogOpen = () => {},
  onSetup = () => {},
  onContentChange,
  includeSignature = false,
  signaturePreviewHtml = "",
  height = 300,
  compact = false,
}) {
  useEffect(() => {
    if (!editorRef.current) {
      return
    }

    syncEditorSignaturePreview(
      editorRef.current,
      includeSignature,
      signaturePreviewHtml
    )
  }, [editorRef, includeSignature, signaturePreviewHtml, editorReady])

  return (
    <>
      <Editor
        apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
        onInit={(_evt, editor) => (editorRef.current = editor)}
        onEditorChange={(_event, editor) =>
          onContentChange?.(editor.getContent())
        }
        init={{
          height,
          menubar: false,
          statusbar: false,
          branding: false,
          plugins: "lists noneditable",
          toolbar: compact
            ? "undo redo | bold italic | bullist numlist | insert-link"
            : [
                "undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify",
                "outdent indent | numlist bullist | insert-link | insert-agent-data",
              ],
          content_style: `
            .email-template-signature-preview {
              margin-top: 1.5em;
              padding-top: 1em;
              border-top: 1px dashed #bdbdbd;
              opacity: 0.9;
              cursor: default;
              user-select: none;
            }
            .email-template-signature-preview img {
              max-width: 100%;
              height: auto;
            }
            body {
              min-height: 100%;
              ${
                compact
                  ? "margin: 8px 12px; font-size: 14px; line-height: 1.55;"
                  : ""
              }
            }
          `,
          setup: (editor) => {
            editor.on("init", () => {
              setEditorReady(true)
            })
            editor.ui.registry.addButton("insert-link", {
              tooltip: "Insert link",
              icon: "link",
              onAction: () => setInsertLinkDialogOpen(true),
            })
            onSetup(editor)
          },
        }}
      />
      <InsertLinkDialog
        open={insertLinkDialogOpen}
        onClose={() => setInsertLinkDialogOpen(false)}
        editorRef={editorRef}
      />
    </>
  )
}

export function useEmailWYSWIGEditorState(editorOpen) {
  const [editorReady, setEditorReady] = useState(false)
  const [insertLinkDialogOpen, setInsertLinkDialogOpen] = useState(false)
  const editorRef = useRef()

  function setEditorBody(
    htmlContents,
    includeSignature = false,
    signaturePreviewHtml = ""
  ) {
    if (!editorReady || !editorRef.current) {
      return
    }

    editorRef.current.setContent(
      stripEmbeddedEmailSignature(
        stripTemplateSignaturePreview(htmlContents || "")
      )
    )
    syncEditorSignaturePreview(
      editorRef.current,
      includeSignature,
      signaturePreviewHtml
    )
  }

  function updateEditorContents(htmlContents) {
    setEditorBody(htmlContents, false, "")
  }

  useEffect(() => {
    if (!editorOpen) {
      setEditorReady(false)
    }
  }, [editorOpen])

  return {
    editorReady,
    setEditorReady,
    insertLinkDialogOpen,
    setInsertLinkDialogOpen,
    editorRef,
    updateEditorContents,
    setEditorBody,
  }
}
